import { cn } from '@/lib/utils';
import { ArrowRight, CheckCircle2, ChevronRight, FileSearch } from 'lucide-react';
import { ListSkeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Button } from '@/components/ui/button';
import { DocIconTile } from './DocChips';
import {
  REVIEW_TAB_HINT,
  REVIEW_TAB_ORDER,
  REVIEW_TAB_SHORT_LABEL,
  tabForItem,
  type ReviewTab,
} from '../config/reviewTabs';
import type { ReviewQueueItem } from '../types/documentCapture.types';

/**
 * Needs-review card (86d3rrfbf; reason-grouped in 86d3rrfdk) — the one place exceptions surface on
 * the landing page. A row opens the Review overlay at that item.
 *
 * Tabs group by REASON, not the server's `Kind` (which mislabels 4 of 6 NetSuite-side reasons). The
 * counts come from the server-scoped queue, so a sales-only user can never infer treasury volume.
 */

/** Rows the rail card previews before collapsing the rest behind "See all" (design bundle). */
const CARD_PREVIEW_MAX = 5;

interface NeedsReviewCardProps {
  counts: { scans: number; netsuite: number; replacements: number; total: number };
  activeTab: ReviewTab;
  items: ReviewQueueItem[];
  loading: boolean;
  error: string | null;
  onTabChange: (tab: ReviewTab) => void;
  onOpenItem: (tab: ReviewTab, reviewItemId: number) => void;
  onRetry: () => void;
}

function countFor(counts: NeedsReviewCardProps['counts'], tab: ReviewTab): number {
  return counts[tab];
}

export function NeedsReviewCard({
  counts,
  activeTab,
  items,
  loading,
  error,
  onTabChange,
  onOpenItem,
  onRetry,
}: NeedsReviewCardProps) {
  // The card shows only the active tab's items (grouped from the full queue).
  const tabItems = items.filter((item) => tabForItem(item) === activeTab);

  const hasRows = !loading && !error && tabItems.length > 0;

  /**
   * A tablist is ONE tab stop and the arrow keys move within it. `role="tab"` promises a screen
   * reader exactly that, so this has to deliver it — the roles without the keys would be a worse
   * lie than the `aria-pressed` they replace.
   */
  function handleTabKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    const delta = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (delta === 0) return;
    event.preventDefault();

    const count = REVIEW_TAB_ORDER.length;
    const nextIndex = (REVIEW_TAB_ORDER.indexOf(activeTab) + delta + count) % count;
    const next = REVIEW_TAB_ORDER[nextIndex];
    onTabChange(next);

    // Focus travels with the selection. `tabIndex` follows `active` on the next render, so leaving
    // focus behind would strand a keyboard user on a tab that is no longer selected — and, once it
    // went to -1, on the only tab they could no longer reach with Tab.
    const tabs = event.currentTarget.parentElement?.querySelectorAll<HTMLElement>('[role="tab"]');
    tabs?.[nextIndex]?.focus();
  }

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="flex items-center gap-2 border-b border-border px-[14px] py-3">
        <FileSearch className="h-4 w-4 flex-shrink-0 text-primary" aria-hidden />
        <span className="flex-1 text-[13.5px] font-bold text-foreground">Needs review</span>
        {counts.total > 0 && (
          <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground">
            {counts.total > 99 ? '99+' : counts.total}
          </span>
        )}
      </div>

      {/*
        A segmented control needs its TRACK (86d3z1n04). The active pill is `bg-card` and this card
        is ALSO `bg-card`, so without a muted surface beneath it the selection was white-on-white:
        the row read as three plain labels with nothing chosen, and the only rendered difference was
        one step of grey on 11.5px text three items apart. Same control as the Second reader
        dialog's `TabsList`, which is where this pattern already lives.

        `h-[26px]` plus the track's 3px padding keeps the row at its original 32px, so the card's
        vertical rhythm does not move. The rule that used to close this row now closes the hint line
        below, so the track is not fighting a border directly beneath it.

        ⚠️ In DARK, `--card` (12%) is darker than `--muted` (20%), so the pill reads recessed rather
        than raised and the shadow does nothing. That is fine — it still separates clearly — but do
        not "fix" it against a light-theme screenshot.
      */}
      <div
        role="tablist"
        aria-label="Review queue tabs"
        className="mx-2.5 my-2 flex gap-0.5 rounded-lg bg-muted p-[3px]"
      >
        {REVIEW_TAB_ORDER.map((tab) => {
          const count = countFor(counts, tab);
          const active = tab === activeTab;
          return (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={active}
              tabIndex={active ? 0 : -1}
              onKeyDown={handleTabKeyDown}
              onClick={() => onTabChange(tab)}
              className={cn(
                'inline-flex h-[26px] flex-1 items-center justify-center gap-[5px] rounded-md text-[11.5px] font-semibold transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                active
                  ? 'bg-card text-foreground shadow-[0_1px_2px_rgba(0,0,0,0.10)]'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {REVIEW_TAB_SHORT_LABEL[tab]}
              {/* Always show the count, including zero. A tab that hides its number makes the
                  reader guess whether it is empty or just unlabelled.
                  Colouring it is the SECOND cue, independent of the pill: if the pill is ever lost
                  again to a background change, the tab does not go silent with it. */}
              <span className={cn('tabular-nums', active ? 'text-primary' : 'text-muted-foreground')}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* The third cue — and the only one that survives high-contrast mode, a theme regression, or
          a screenshot pasted into chat. */}
      <div className="border-b border-border px-3 pb-2 text-[11px] text-muted-foreground">
        {REVIEW_TAB_HINT[activeTab]}
      </div>

      {loading ? (
        <div className="p-3">
          <ListSkeleton rows={3} />
        </div>
      ) : error ? (
        <div className="p-3">
          <EmptyState
            title="Couldn’t load the queue"
            description={error}
            action={
              <Button variant="outline" size="sm" onClick={onRetry}>
                Try again
              </Button>
            }
          />
        </div>
      ) : !hasRows ? (
        <div className="p-3">
          <EmptyState
            icon={CheckCircle2}
            title="Nothing needs you"
            description="Everything scanned so far was captured and linked automatically."
          />
        </div>
      ) : (
        <>
          <ul className="max-h-[340px] overflow-y-auto">
            {tabItems.slice(0, CARD_PREVIEW_MAX).map((item) => (
              <li key={item.id}>
                {/* A queue row must READ as an actionable item, not an info callout (user feedback
                    2026-07-27): the 3px amber spine marks the whole list as exceptions, the tile
                    names the family, the chevron says "opens something". */}
                <button
                  type="button"
                  onClick={() => onOpenItem(activeTab, item.id)}
                  aria-label={`Review: ${item.docNumber ?? 'unidentified scan'}`}
                  className="flex w-full cursor-pointer gap-2.5 border-l-[3px] border-t border-l-warning border-t-border px-[14px] py-[11px] text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                >
                  <DocIconTile value={item.effectivePrefix} tone="warning" size={30} radius={8} icon={15} />
                  <span className="min-w-0 flex-1">
                    {/* Name the SCAN, not its absence. "Unidentified scan" was the same string on
                        every unidentified row, so five of them were indistinguishable; the scan
                        number is what the reviewer matches against the overlay and the paper. */}
                    <span className="block truncate font-mono text-[12.5px] font-semibold text-foreground">
                      {item.docNumber ?? `Scan #${item.fileId ?? item.id}`}
                    </span>
                    {/* Verbatim server sentence — plain language, written when the item was raised.
                        NO `block` here: `line-clamp-2` sets `display:-webkit-box`, and a `block`
                        alongside it wins and silently disables the clamp (rows ran to three lines). */}
                    <span className="mt-0.5 line-clamp-2 text-[11px] leading-[1.4] text-muted-foreground">
                      {item.reasonDetail}
                    </span>
                  </span>
                  <ChevronRight
                    className="h-[15px] w-[15px] shrink-0 self-center text-muted-foreground"
                    aria-hidden="true"
                  />
                </button>
              </li>
            ))}
          </ul>

          {/*
            The card is a PREVIEW, not the queue. Past five rows it stops being scannable at a
            glance and turns the page's right rail into a second work surface competing with the
            overlay that is actually built for the job — so the overflow becomes one honest link
            rather than an ever-growing list.
          */}
          {tabItems.length > CARD_PREVIEW_MAX && (
            <button
              type="button"
              onClick={() => onOpenItem(activeTab, tabItems[0].id)}
              className="flex w-full items-center justify-center gap-1.5 border-t border-border py-2.5 text-[12px] font-semibold text-primary transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            >
              See all {tabItems.length}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </button>
          )}
        </>
      )}
    </div>
  );
}
